export interface NoteSection {
  id: string;
  level: 2 | 3;
  title: string;
  content: string[];
}

export interface NoteDetail {
  id: string;
  title: string;
  subtitle: string;
  type: string;
  date: string;
  wordCount: number;
  tags: string[];
  sections: NoteSection[];
}

export const noteDetails: NoteDetail[] = [
  {
    id: "n1",
    title: "量子纠错算法推导笔记",
    subtitle: "从 Shor 码出发，推导 9-qubit 纠错逻辑，记录了稳定子形式化过程中的关键步骤与直觉理解",
    type: "笔记",
    date: "2026-04-24",
    wordCount: 2340,
    tags: ["量子计算", "纠错码"],
    sections: [
      {
        id: "s1",
        level: 2,
        title: "背景：为什么需要量子纠错",
        content: [
          "量子比特极易受到环境噪声的干扰，退相干时间通常在微秒量级。在实际计算中，即便是极小的错误率也会在深度电路中累积，最终导致计算结果完全不可信。",
          "与经典纠错不同，量子态无法被直接复制（不可克隆定理），因此经典的三重冗余编码方案在量子系统中无法直接套用。量子纠错必须在不测量（坍缩）信息比特的前提下检测并纠正错误。",
          "Peter Shor 于 1995 年提出的 9-qubit 码是第一个完整的量子纠错方案，能够同时纠正比特翻转错误（X 错误）和相位翻转错误（Z 错误）。"
        ]
      },
      {
        id: "s2",
        level: 2,
        title: "Shor 码的编码结构",
        content: [
          "Shor 码将 1 个逻辑量子比特编码到 9 个物理量子比特中。其核心思想是嵌套编码：先用 3 个量子比特对相位翻转进行保护，再用 3 组各 3 个量子比特对比特翻转进行保护。",
          "逻辑 |0⟩ 态被编码为：|0_L⟩ = (|000⟩ + |111⟩)⊗³ / 2√2，逻辑 |1⟩ 态被编码为：|1_L⟩ = (|000⟩ - |111⟩)⊗³ / 2√2。",
          "这种双重嵌套结构使得 X 错误和 Z 错误可以被独立检测。关键在于，检测过程使用辅助量子比特（ancilla）与稳定子测量，不会坍缩逻辑量子比特的状态。"
        ]
      },
      {
        id: "s3",
        level: 2,
        title: "稳定子形式化",
        content: [
          "稳定子形式是理解量子纠错最强大的数学工具。对于 Shor 码，稳定子群由 8 个独立生成元组成，分别对应比特翻转检测（Z 型稳定子）和相位翻转检测（X 型稳定子）。",
          "Z 型稳定子：g₁ = Z₁Z₂, g₂ = Z₂Z₃, g₃ = Z₄Z₅, g₄ = Z₅Z₆, g₅ = Z₇Z₈, g₆ = Z₈Z₉，这 6 个算符检测各组内的比特翻转错误。",
          "X 型稳定子：g₇ = X₁X₂X₃X₄X₅X₆, g₈ = X₄X₅X₆X₇X₈X₉，这 2 个算符负责检测跨组的相位翻转错误。测量这 8 个稳定子的本征值（±1）形成 8 比特的错误综合征（syndrome），唯一标识了单量子比特错误的位置和类型。"
        ]
      },
      {
        id: "s4",
        level: 2,
        title: "纠错操作流程",
        content: [
          "完整的纠错周期分为三步：综合征提取 → 错误解码 → 纠错操作。综合征提取通过 CNOT 门将稳定子信息转移到辅助比特，然后测量辅助比特获得综合征值，整个过程不破坏逻辑量子比特。",
          "错误解码阶段，根据 8 位综合征查找对应的错误类型和位置。Shor 码可以纠正任意单量子比特错误，即 I, X, Y, Z 中的任意一种作用于 9 个物理比特中的任意一个。",
          "值得注意的是，实际实现中测量操作本身也会引入错误（容错量子计算的挑战所在）。这要求纠错码不仅能纠正数据错误，还需要能容忍测量错误，这引出了阈值定理和拓扑码（如表面码）的后续发展。"
        ]
      },
      {
        id: "s5",
        level: 2,
        title: "个人理解与思考",
        content: [
          "Shor 码最让我觉得精妙的地方在于：它把「无法复制」这个约束转化为了「测量辅助信息而非信息本身」的策略。稳定子测量本质上是在问系统「你的错误综合征是什么」，而不是「你处于哪个量子态」。",
          "从信息论角度看，9-qubit 的冗余代价换来的是针对任意单比特错误的完全保护，错误更正能力与逻辑量子比特的保真度提升成正比。但资源开销较大，这也是为什么现代量子纠错研究重点转向了表面码——它在二维格点上用接近最优的编码效率实现了类似的纠错能力。",
          "下一步打算深入阅读 Gottesman 的 stabilizer formalism 原始论文，以及了解容错量子门（如 transversal gates）的设计约束。"
        ]
      }
    ]
  },
  {
    id: "n2",
    title: "分子动力学势能面 LaTeX 推导",
    subtitle: "对 Born-Oppenheimer 近似条件下的势能面采样方法进行数学推导，含完整公式链及误差分析",
    type: "笔记",
    date: "2026-04-21",
    wordCount: 1870,
    tags: ["分子动力学", "势能面"],
    sections: [
      {
        id: "s1",
        level: 2,
        title: "Born-Oppenheimer 近似的数学基础",
        content: [
          "在分子系统的量子力学描述中，完整哈密顿量包含核动能 T_N、电子动能 T_e 和库仑相互作用项 V。由于核质量远大于电子质量（质子质量约为电子质量的 1836 倍），核的运动速度远慢于电子。",
          "Born-Oppenheimer 近似的核心假设：在核运动的任意构型 R 下，电子几乎瞬时调整到对应的本征态。因此可以将分子波函数近似分离为电子波函数 ψ_e(r;R) 和核波函数 χ_N(R) 的乘积。",
          "在固定核坐标 R 下求解电子薛定谔方程 H_e ψ_e = E_e(R) ψ_e，电子能量 E_e(R) 作为核坐标 R 的函数，就定义了势能面（Potential Energy Surface, PES）。"
        ]
      },
      {
        id: "s2",
        level: 2,
        title: "势能面的采样策略",
        content: [
          "直接量子化学计算（如 DFT 或 CCSD(T)）只能给出有限采样点处的能量和梯度信息。为了在整个构型空间上使用势能面，需要构建连续的解析或数值近似。",
          "经典力场方法使用简单解析函数（如 Lennard-Jones 势、Morse 势）拟合少量参数，计算效率极高但准确性有限。机器学习势（MLP）近年来成为研究热点，通过神经网络或高斯过程回归拟合大规模 DFT 数据集，兼顾精度与效率。",
          "主动学习采样策略（Active Learning）是当前最前沿的方向：利用不确定性估计（如集成方法的预测方差）识别构型空间中信息量最大的采样点，迭代优化训练集，在最少计算代价下达到目标精度。"
        ]
      },
      {
        id: "s3",
        level: 2,
        title: "误差分析与精度评估",
        content: [
          "势能面的误差来源主要有两类：系统误差（来自量子化学方法本身的近似，如 DFT 交换关联泛函的选择）和统计误差（来自训练数据有限和模型表达能力不足）。",
          "通过留出测试集（hold-out test set）计算均方根误差（RMSE）是标准评估方式，但更重要的是在实际模拟任务（如预测晶格常数、振动频率、反应势垒）上的性能表现。",
          "值得注意的是外推性问题：机器学习势在训练集覆盖范围外的构型上精度会急剧下降。需要结合不确定性量化（UQ）方法对预测可靠性进行实时监控。"
        ]
      }
    ]
  },
  {
    id: "n3",
    title: "材料带隙对比图注",
    subtitle: "对 DFT 计算所得 12 种钙钛矿材料带隙数据的图像注解，标注关键异常点与可能的物理解释",
    type: "笔记",
    date: "2026-04-18",
    wordCount: 640,
    tags: ["DFT", "钙钛矿"],
    sections: [
      {
        id: "s1",
        level: 2,
        title: "数据来源与计算设置",
        content: [
          "本图集整理自 12 种 ABX₃ 型钙钛矿材料（A = Cs/MA/FA，B = Pb/Sn，X = I/Br/Cl 混合卤素）的 DFT 计算结果。使用 VASP 软件包，交换关联泛函选用 HSE06 + SOC（自旋轨道耦合），平面波截断能 500 eV，k 点网格 6×6×6 Monkhorst-Pack。",
          "计算带隙与实验值的系统偏差约为 0.1-0.2 eV，在合理范围内。需要注意含 Sn 体系因 Sn²⁺ 的氧化还原不稳定性，计算结果与实验的偏差相对较大。"
        ]
      },
      {
        id: "s2",
        level: 2,
        title: "关键异常点标注",
        content: [
          "图中红色标记的三个数据点（CsSnI₃、MASnBr₃、FASnI₃）的计算带隙明显偏低（< 1.0 eV），与同族铅基材料相比下降约 0.8-1.2 eV。这与 Sn 的 5s 孤对电子参与价带顶的构成有关，导致价带顶能量显著抬升。",
          "蓝色虚线圈出的 MAPbI₃ 系列数据点显示出随 Br 含量增加带隙线性增大的趋势，斜率约为 0.6 eV/unit-composition，与 Vegard 定律预测基本吻合，可用于带隙工程设计。"
        ]
      },
      {
        id: "s3",
        level: 2,
        title: "物理机制解释",
        content: [
          "钙钛矿带隙的主要调控机制：(1) 化学组分替换，通过改变 B 位金属和 X 位卤素调控带隙宽度；(2) 结构畸变，温度或压力导致的八面体倾斜会改变 B-X-B 键角，进而调制带宽；(3) 量子限制效应，在低维钙钛矿（2D、准 2D）中尤为显著。",
          "后续需要结合拉曼光谱数据验证结构畸变的程度，并与 GW 方法计算结果对比，评估 HSE06 方案在描述这类材料时的系统性误差分布。"
        ]
      }
    ]
  },
  {
    id: "n4",
    title: "AI 竞品阅读随记",
    subtitle: "阅读 12 款主流 AI 研究工具后汇总的使用感受与差异化分析，记录产品逻辑与研究流程契合度",
    type: "笔记",
    date: "2026-04-14",
    wordCount: 1120,
    tags: ["竞品分析", "AI工具"],
    sections: [
      {
        id: "s1",
        level: 2,
        title: "测评工具清单",
        content: [
          "本次测评覆盖 12 款主流 AI 研究辅助工具，涵盖文献综述类（Elicit、Consensus、Semantic Scholar AI、ResearchRabbit）、写作辅助类（Jenni AI、Paperpal、Writefull）、全能工作台类（Perplexity、ChatGPT、Claude）以及国内工具（Kimi、通义千问学术版）。",
          "测评维度包括：文献检索准确率、内容生成质量、引用可追溯性、工作流集成程度、UI 友好度和定价合理性，每个维度按 1-5 分评分。"
        ]
      },
      {
        id: "s2",
        level: 2,
        title: "核心差异化发现",
        content: [
          "文献溯源能力是分水岭。Elicit 和 Consensus 在引用准确性上显著优于通用 LLM，几乎不产生幻觉引用，但覆盖范围相对局限（主要是英文文献）。ChatGPT/Claude 写作流畅但引用可靠性差，需要人工核实，不适合作为学术写作的直接输出工具。",
          "工作流整合度普遍不足。测评中没有任何一款工具能无缝覆盖从文献发现 → 深度阅读 → 数据分析 → 报告生成的完整科研链路。Perplexity 在信息聚合上最出色，但缺乏个人知识库管理能力；ResearchRabbit 在文献发现网络可视化上独树一帜，但写作功能几乎为零。",
          "中文科研场景支持薄弱。国内工具在中文文献检索上有优势，但在数理公式渲染、LaTeX 输出和科研规范性（如引用格式）上与国际工具差距明显。"
        ]
      },
      {
        id: "s3",
        level: 2,
        title: "对 ScienceOne 的启示",
        content: [
          "机会窗口在「整合」：没有竞品真正打通了全链路。文献 → 知识库 → 推理 → 成果这个闭环，在竞品中都是割裂的。ScienceOne 的差异化核心应该是让这个链路流畅，而不是在某个单点功能上与专项工具竞争。",
          "认知网络是真正的护城河：个人知识图谱随使用时间积累的网络效应，是竞品最难复制的资产。重点是让输入（对话、文献、笔记）自动沉淀到认知网络，而不是让用户手动整理。"
        ]
      }
    ]
  },
  {
    id: "n5",
    title: "统计力学期末复习 LaTeX",
    subtitle: "配分函数、系综理论、涨落定理的全量推导，配合 Feynman 路径积分的简化注记",
    type: "笔记",
    date: "2026-04-10",
    wordCount: 3210,
    tags: ["统计力学", "热力学"],
    sections: [
      {
        id: "s1",
        level: 2,
        title: "正则系综与配分函数",
        content: [
          "正则系综描述与热库接触且温度固定为 T 的系统。系统处于能量本征态 E_i 的概率由 Boltzmann 分布给出：P_i = e^{-βE_i} / Z，其中 β = 1/(k_BT) 是逆温度，Z = Σ_i e^{-βE_i} 是正则配分函数。",
          "所有热力学量都可以从配分函数导出：内能 U = -∂ln Z/∂β，亥姆霍兹自由能 F = -k_BT ln Z，熵 S = -∂F/∂T = k_B(ln Z + βU)，压强 p = -∂F/∂V。这种统一框架是统计力学最优雅的特征之一。"
        ]
      },
      {
        id: "s2",
        level: 2,
        title: "巨正则系综",
        content: [
          "当系统不仅与热库交换能量，还与粒子库交换粒子时，需要使用巨正则系综。引入化学势 μ，巨配分函数定义为 Ξ = Σ_{N,i} e^{-β(E_{N,i} - μN)}。",
          "对于量子理想气体，巨正则系综给出了费米-狄拉克分布（费米子）和玻色-爱因斯坦分布（玻色子），两者在高温极限下均退化为麦克斯韦-玻尔兹曼分布，体现了量子统计向经典统计的过渡。"
        ]
      },
      {
        id: "s3",
        level: 2,
        title: "涨落与响应函数",
        content: [
          "涨落-耗散定理是非平衡统计力学的基石：系统对外部微扰的响应（耗散）与平衡态的自发涨落之间存在深刻联系。对于能量涨落，⟨(ΔE)²⟩ = k_B T² C_V，其中 C_V 是定容热容。",
          "这一关系意味着：比热容越大的系统，其能量涨落越剧烈。在相变点附近，涨落发散，导致相关长度趋向无穷（临界涨落），这是临界现象和重整化群理论的起点。"
        ]
      },
      {
        id: "s4",
        level: 2,
        title: "Feynman 路径积分简记",
        content: [
          "配分函数可以用路径积分表示：Z = ∫D[x(τ)] exp(-S_E[x]/ℏ)，其中积分遍历所有满足周期性边界条件 x(0) = x(ℏβ) 的虚时路径，S_E 是欧几里得作用量。",
          "这种表述建立了统计力学与量子场论的深刻联系：有限温度量子场论在紧化的虚时维度上与经典统计场论等价，是凝聚态和粒子物理共享数学工具的基础。"
        ]
      }
    ]
  },
  {
    id: "n6",
    title: "实验装置示意草图批注",
    subtitle: "针对低温超导测量台架图像的手绘批注版本，标注各模块热导通路与屏蔽层结构",
    type: "图片",
    date: "2026-04-06",
    wordCount: 380,
    tags: ["超导", "实验装置"],
    sections: [
      {
        id: "s1",
        level: 2,
        title: "装置整体结构",
        content: [
          "本批注针对稀释制冷机（Dilution Refrigerator）测量台架的示意图，温区从室温（300K）到混合腔（约 10mK）分为五级：77K（液氮屏蔽）、4K（液氦）、Still（约 700mK）、冷板（约 100mK）和混合腔。",
          "红色箭头标注了各级间的热导通路：铜绝缘线缆是主要的热漏路径之一，需要通过热锚（heat sink）在每一温级将多余热量导走，防止热负载传递到更冷的温区。"
        ]
      },
      {
        id: "s2",
        level: 2,
        title: "电磁屏蔽层分析",
        content: [
          "蓝色虚线圈出了三层同轴屏蔽结构：外层 μ 金属屏蔽（针对低频磁场）、中层铝合金超导屏蔽（针对高频电磁干扰）和内层铜法拉第笼（针对电场噪声）。超导屏蔽在 Tc 以下形成迈斯纳效应排磁场，但需要注意磁通钉扎问题。",
          "绿色标注指出了同轴电缆的走线规范：高频信号线（> 1 GHz）使用银镀铜超导同轴电缆，DC 偏置线使用低热导率的 NbTi 超导线，两者需要严格隔离以防止串扰。"
        ]
      }
    ]
  }
];
